import type { Env } from './env.js';
import type { AuthState } from './msal.js';
import { PRODUCT_PASSKEY_ROUTES } from './passkeyContract.js';
import type {
  EnrolledPasskey,
  EnrollPasskeyInput,
  PasskeyRoutes,
  PostJson,
} from './passkeyContract.js';

/**
 * Which pair of functions the sign-in gate drives.
 *
 * **The dynamic `import()` below is load-bearing and must not become a static
 * one.** It is what keeps ADR-0027's invariant true: a mocked build never
 * downloads MSAL, so `pnpm -r test` and `pnpm dev` need no Entra configuration
 * at all (Article 5).
 *
 * That invariant is also why **the barrel does not re-export `msal.ts`**. A
 * static `export … from './msal.js'` in `index.ts` would pull MSAL into every
 * consumer's bundle the moment they imported anything at all from this package
 * — silently, and with no test that would notice. This module is the only
 * sanctioned reference to it, and its laziness is the whole reason it exists
 * rather than each app writing the switch again.
 *
 * Lifted out of `apps/web/src/Bootstrap.tsx`, where it was two free functions
 * closing over that app's `env` and importing that app's `mockAuth`.
 *
 * **The mock pair is a parameter rather than an import, and that is the seam
 * that decides what this package is.** A mock identity is a fixture: `apps/web`
 * signs in as a sergeant whose name matches its own mocked `/me`, and the
 * operator console signs in as somebody who is on no agency's roster at all.
 * Neither belongs to a shared package, and a package that picked one would be
 * choosing an app's test data for it. What is genuinely shared is the *switch*
 * and the laziness — which is exactly what is left here.
 */
export interface MockAuth {
  /**
   * The MSAL pair, and **optional since M11** — `apps/web` supplies neither.
   *
   * _Amended._ This module's header argues against a second driver on the
   * grounds that what is shared is "the switch and the laziness", and that a
   * second one would be a second copy of that decision. That still holds, and
   * it is why the pair became optional rather than moving to a `passkeyDriver`
   * of its own. What changed underneath the argument is that the two consumers
   * stopped needing the same libraries: the passcode is a form on `apps/web`'s
   * landing page now (SYS-7), so nothing there reaches `msal.js`, while the
   * operator console still signs in by redirect.
   *
   * Absent means the app cannot perform that act at all. `authDriver` refuses
   * rather than degrading, because a mocked build that silently resolved would
   * be a sign-in that appears to work and hands back nobody.
   */
  verifyAuth?: () => Promise<AuthState>;
  beginSignIn?: () => Promise<never>;
  /**
   * Enrolling a passkey, without one (USR-11).
   *
   * **It still takes `postJson`, and still uses it.** The point of the mock
   * is to skip `navigator.credentials`, which jsdom does not have and a mocked
   * build must not download a library for — not to skip the network, which MSW
   * is already answering. So the mocked screen lists credentials that really
   * came back from a handler, and only the ceremony is imaginary.
   *
   * `apps/admin` implements this by throwing: the operator console enrols
   * nothing, and a stub that quietly succeeded would be a worse answer than one
   * that says so.
   */
  enrollPasskey: (postJson: PostJson, input: EnrollPasskeyInput) => Promise<EnrolledPasskey>;
  /**
   * Signing in with a passkey (SYS-8).
   *
   * Under mocks this is `apps/web`'s fixture, which reloads the page the way
   * `beginSignIn` does — there is no authenticator to gesture at, and the
   * honest imitation of "the tab comes back signed in" is a reload.
   */
  signInWithPasskey: (postJson: PostJson) => Promise<{ restricted: boolean }>;
  platformAuthenticatorAvailable: () => Promise<boolean>;
}

export interface AuthDriverOptions {
  env: Env;
  /** This app's mocked identity. Reached only when `VITE_USE_MOCKS` is true. */
  mock: MockAuth;
  /**
   * Which endpoints verify this app's passkey ceremonies (**ADR-0085**).
   *
   * **The ceremony is shared and the routes are not.** `apps/admin` runs the
   * identical WebAuthn flow against `operator/*`, because those routes resolve
   * an operator rather than a principal — so this is a parameter and there is
   * one copy of the ceremony rather than two. Defaults to the product's, which
   * is what `apps/web` passes by saying nothing.
   */
  routes?: PasskeyRoutes;
}

export interface AuthDriver {
  /** Never navigates. Resolves to what is known about the caller. */
  verifyAuth: () => Promise<AuthState>;
  /** Never resolves — it hands the tab to Entra. */
  beginSignIn: () => Promise<never>;
  /**
   * The whole registration ceremony (USR-11). Rejects if the user cancels.
   *
   * **Here rather than in a driver of its own**, although it is an act inside
   * the app rather than at the sign-in gate. What this module is actually for
   * is the *switch* and the *laziness* — one place that decides whether an
   * authentication library is downloaded at all — and a second driver would be
   * a second copy of that decision, which is the thing Article 3 asks us not to
   * have two of.
   */
  enrollPasskey: (postJson: PostJson, input: EnrollPasskeyInput) => Promise<EnrolledPasskey>;
  /** One gesture, one session. No redirect, no code, no address typed first. */
  signInWithPasskey: (postJson: PostJson) => Promise<{ restricted: boolean }>;
  /** Whether this device has a built-in authenticator. Phrases the question; never answers it. */
  platformAuthenticatorAvailable: () => Promise<boolean>;
}

/**
 * An act this app's mock does not implement, reached anyway.
 *
 * A rejection rather than a silent resolve: under mocks the alternative is a
 * sign-in that appears to succeed and produces nobody, which is the failure
 * mode hardest to see from a test.
 */
function refuse(act: string): Promise<never> {
  return Promise.reject(
    new Error(`This app's mocked identity does not implement ${act}, so it cannot be used here.`),
  );
}

export function authDriver({
  env,
  mock,
  routes = PRODUCT_PASSKEY_ROUTES,
}: AuthDriverOptions): AuthDriver {
  return {
    verifyAuth: () => {
      if (env.VITE_USE_MOCKS) return mock.verifyAuth?.() ?? refuse('verifyAuth');

      return import('./msal.js').then(async (msal) => await msal.verifyAuth(env));
    },

    beginSignIn: () => {
      if (env.VITE_USE_MOCKS) return mock.beginSignIn?.() ?? refuse('beginSignIn');

      return import('./msal.js').then(async (msal) => await msal.beginSignIn(env));
    },

    enrollPasskey: (postJson, input) => {
      if (env.VITE_USE_MOCKS) return mock.enrollPasskey(postJson, input);

      return import('./passkey.js').then(
        async (passkey) => await passkey.enrollPasskey(postJson, input, routes),
      );
    },

    signInWithPasskey: (postJson) => {
      if (env.VITE_USE_MOCKS) return mock.signInWithPasskey(postJson);

      return import('./passkey.js').then(
        async (passkey) => await passkey.signInWithPasskey(postJson, routes),
      );
    },

    platformAuthenticatorAvailable: () => {
      if (env.VITE_USE_MOCKS) return mock.platformAuthenticatorAvailable();

      return import('./passkey.js').then(
        async (passkey) => await passkey.platformAuthenticatorAvailable(),
      );
    },
  };
}
